import { FetchResult } from '@apollo/client'
import { css } from '@emotion/react'
import { Button, Cell, Grid, HFlow, Theme, Tooltip, useTheme } from 'bold-ui'
import {
  CpfField,
  Form,
  FormRenderProps,
  PecFormApi,
  resetAllFieldStates,
  SubmitButton,
  TextField,
} from 'components/form'
import { CnpjField } from 'components/form/field/CnpjField'
import { TipoPessoaGroupField } from 'components/form/field/TipoPessoaRadioGroupField'
import { useSalvarCredencialIntegracaoOldMutation } from 'graphql/hooks.generated'
import { RecursoIntegracaoEnum, SalvarCredencialIntegracaoOldMutation, TipoPessoaEnum } from 'graphql/types.generated'
import { useMemo, useState } from 'react'
import { isHttpsConnection } from 'util/https'
import { metaPath } from 'util/metaPath'
import { cnpj, cpf, createValidator, email, maxLength, minLength, required, validate } from 'util/validation'

import {
  CredencialIntegracaoGeradaModalOld,
  CredencialIntegracaoUserPasswordOld,
} from './CredencialIntegracaoGeradaModalOld'

interface CredencialIntegracaoFormModelOld {
  nomeResponsavel: string
  tipoPessoa: TipoPessoaEnum
  cpfCnpj: string
  email: string
  nomeCredencial: string
}

const path = metaPath<CredencialIntegracaoFormModelOld>()

const validator = createValidator<CredencialIntegracaoFormModelOld>(
  {
    nomeCredencial: [required, minLength(3), maxLength(100)],
    nomeResponsavel: [required, minLength(5), maxLength(100)],
    tipoPessoa: [required],
    cpfCnpj: [required],
    email: [required, email, minLength(6), maxLength(100)],
  },
  (values, errors) => {
    if (values.tipoPessoa === TipoPessoaEnum.FISICA) {
      errors.cpfCnpj = errors.cpfCnpj ?? validate(values.cpfCnpj, cpf)
    }
    if (values.tipoPessoa === TipoPessoaEnum.JURIDICA) {
      errors.cpfCnpj = errors.cpfCnpj ?? validate(values.cpfCnpj, cnpj)
    }

    return errors
  }
)

interface CredenciaisIntegracaoFormPropsOld {
  readonly refetch: () => void
}

export function CredenciaisIntegracaoFormOld({ refetch }: CredenciaisIntegracaoFormPropsOld) {
  const [save] = useSalvarCredencialIntegracaoOldMutation()
  const theme = useTheme()
  const styles = createStyles(theme)

  const [isModelOpen, setIsModelOpen] = useState(false)
  const [userPassword, setUserPassword] = useState<CredencialIntegracaoUserPasswordOld>()

  const initialValues: Partial<CredencialIntegracaoFormModelOld> = useMemo(
    () => ({ tipoPessoa: TipoPessoaEnum.FISICA }),
    []
  )

  const onSubmitSuccess = (
    payload: FetchResult<SalvarCredencialIntegracaoOldMutation>,
    form: PecFormApi<CredencialIntegracaoFormModelOld>
  ) => {
    refetch()
    setUserPassword(payload.data.salvarCredencialIntegracao.userPassword)
    setIsModelOpen(true)
    setTimeout(() => {
      form.reset()
      resetAllFieldStates(form)
    })
  }

  const handleSubmit = (
    values: CredencialIntegracaoFormModelOld,
    form: PecFormApi<CredencialIntegracaoFormModelOld>
  ) => {
    if (isHttpsConnection) {
      return save({
        variables: { input: { ...values, recursos: [RecursoIntegracaoEnum.RECEBIMENTO_EXTERNO_FICHAS] } },
      }).then((payload) => onSubmitSuccess(payload, form))
    }
  }

  const handleCloseIntegracaoGeradaModal = () => {
    setIsModelOpen(false)
    setUserPassword(null)
  }

  const renderForm = (renderProps: FormRenderProps<CredencialIntegracaoFormModelOld>) => {
    const isPessoaFisica = renderProps.values.tipoPessoa === TipoPessoaEnum.FISICA

    const onReset = () => {
      renderProps.form.reset()
      resetAllFieldStates(renderProps.form)
    }

    return (
      <form noValidate>
        <Grid>
          <Cell size={12}>
            <TipoPessoaGroupField name={path.tipoPessoa} required />
          </Cell>
          <Cell size={5}>
            <TextField name={path.nomeResponsavel} label='Responsável' required />
          </Cell>
          <Cell size={7}>
            {isPessoaFisica ? (
              <CpfField name={path.cpfCnpj} label='CPF' required />
            ) : (
              <CnpjField name={path.cpfCnpj} label='CNPJ' required />
            )}
          </Cell>
          <Cell size={5}>
            <TextField name={path.email} label='E-mail' required />
          </Cell>
          <Cell size={7} alignSelf='flex-start'>
            <TextField name={path.nomeCredencial} label='Nome da credencial' required />
          </Cell>
          <Cell size={12}>
            <HFlow justifyContent='flex-end'>
              <Button size='small' onClick={onReset}>
                Limpar campos
              </Button>
              <Tooltip
                text={
                  !isHttpsConnection && 'Para gerar credencias é necessário que a instalação possua HTTPS configurado.'
                }
              >
                <SubmitButton size='small' handleSubmit={renderProps.handleSubmit} disabled={!isHttpsConnection}>
                  Gerar credencial
                </SubmitButton>
              </Tooltip>
            </HFlow>
          </Cell>
        </Grid>
      </form>
    )
  }

  return (
    <div css={styles.box}>
      <CredencialIntegracaoGeradaModalOld
        userPassword={userPassword}
        isOpen={isModelOpen}
        onClose={handleCloseIntegracaoGeradaModal}
      />
      <Form<CredencialIntegracaoFormModelOld>
        initialValues={initialValues}
        render={renderForm}
        validate={validator}
        onSubmit={handleSubmit}
      />
    </div>
  )
}

const createStyles = (theme: Theme) => ({
  box: css`
    padding: 1rem;
    border: 1px solid ${theme.pallete.divider};
  `,
})
